import {
  ChangeEvent,
  cloneElement,
  Dispatch,
  FC,
  ReactElement,
  SetStateAction,
  useEffect,
  useState
} from 'react'
import { Button } from '@progress/kendo-react-buttons'
import {
  Input,
  InputChangeEvent,
  TextArea,
  TextAreaChangeEvent
} from '@progress/kendo-react-inputs'
import { Grid, GridColumn } from '@progress/kendo-react-grid'
import {
  Card,
  CardBody,
  CardHeader,
  CardSubtitle,
  CardTitle,
  GridLayout,
  GridLayoutItem
} from '@progress/kendo-react-layout'

import { HearingReplyItemHistoryListGateway } from '../../../gateway/supplier/HearingReplyItemHistoryListGateway'
import { useAppSelector } from '../../../stores'
import produce from 'immer'
import { BlobFileService } from '../../../services/io/BlobFileService'
import {
  GetSupplierHearingReplyItemHistoryListRes,
  GetSupplierHearingReplyItemSummaryRes
} from '@gateway.Supplier'
import { ErrorMessage } from '../../../components/parts'
import { MessageProvider } from '../../../services'
import dayjs from 'dayjs'

export interface ConfirmReplyHeaderProps {
  mode?: string
  handleOnId: Dispatch<SetStateAction<string>>
  handleOnArgslist: Dispatch<SetStateAction<string[]>>
  handleOnMessageKey: Dispatch<SetStateAction<string>>
  handleOnConfirmDialogshow: Dispatch<SetStateAction<boolean>>
  supplierHearing:
  | GetSupplierHearingReplyItemSummaryRes['supplierHearing']
  | undefined
  handleOnSupplierHearing: Dispatch<
  SetStateAction<GetSupplierHearingReplyItemSummaryRes['supplierHearing'] | undefined>
  >
  setNotificationMessages: React.Dispatch<React.SetStateAction<ErrorMessage[]>>
  setErrorMessages: React.Dispatch<React.SetStateAction<ErrorMessage[]>>
  attachmentFile: File | undefined
  setAttachmentFile: React.Dispatch<React.SetStateAction<File | undefined>>
  setAttachmentFileName: React.Dispatch<React.SetStateAction<string | undefined>>
  lastMonthFlg: boolean
  isNotOwnBranch: boolean
  setIsChanged: React.Dispatch<React.SetStateAction<boolean>>
  hearingSheetFlg: number
}

const rowRender = (trElement: ReactElement<HTMLTableRowElement>) => {
  const style = { fontSize: '0.8em', fontWeight: 'bold' }
  const trProps: any = { style }
  return cloneElement(trElement, { ...trProps })
}

export const ConfirmReplyHeader: FC<ConfirmReplyHeaderProps> = (props) => {
  const mode =
    props.supplierHearing?.replySts === '回答済'
      ? '2'
      : props.supplierHearing?.replySts === '確認済'
        ? '3'
        : '1'

  const {
    simulationYm,
    supplierCd,
    supplierNm,
    factoryCd,
    expenseCd,
    expense,
    branchNm,
    factoryNm,
    replyYm,
    supplierBranchCd,
    supplierBranchNm
  } = useAppSelector((state) => state.confirmreply.confirmReplyInfo)

  const userProp = useAppSelector((state) => state.common.userProp)

  const [historyList, setHistoryList] = useState<
  GetSupplierHearingReplyItemHistoryListRes['replyStsHistoryList']
  >([])

  useEffect(() => {
    // 業者ヒアリング回答確認-ステータス履歴取得
    HearingReplyItemHistoryListGateway.getSupplierHearingReplyItemHistoryList(
      simulationYm,
      supplierCd,
      factoryCd,
      expenseCd,
      supplierBranchCd
    )
      .then((res) => {
        if (!res.error) {
          const stsHistoryList = res.data.replyStsHistoryList.map(sh => {
            const date = new Date(sh.stsUpdDtm)
            sh.stsUpdDtm = `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`
            return sh
          })
          setHistoryList(stsHistoryList)
        } else {
          if (Array.isArray(res.messages) && res.messages.length > 0) {
            const errorMessages = res.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            props.setErrorMessages(errorMessages)
          }
        }
      })
      .finally(() => {})
  }, [expenseCd, factoryCd, mode, simulationYm, supplierCd])

  /**
   * 回答コメント
   */
  const changeReplyComment = (event: TextAreaChangeEvent) => {
    props.setIsChanged(true)
    props.handleOnSupplierHearing(
      produce((draft) => {
        if (draft !== undefined) {
          draft.replyComment = event.value
        }
      })
    )
  }

  /**
   * 回答者(姓)
   */
  const changeReplyNmLast = (event: InputChangeEvent) => {
    props.setIsChanged(true)
    props.handleOnSupplierHearing(
      produce((draft) => {
        if (draft !== undefined) {
          draft.replyNmLast = event.value
        }
      })
    )
  }

  /**
   * 回答者(名)
   */
  const changeReplyNmFirst = (event: InputChangeEvent) => {
    props.setIsChanged(true)
    props.handleOnSupplierHearing(
      produce((draft) => {
        if (draft !== undefined) {
          draft.replyNmFirst = event.value
        }
      })
    )
  }

  /** 回答送信 */
  const answerTransmission = () => {
    if (
      props.supplierHearing?.addStsCdSupplierDecisionBuilding === '3' &&
      props.supplierHearing?.addStsCdSupplierEstimationBuilding === '3' &&
      props.supplierHearing?.addStsCdOtherCompanyBuilding === '3' &&
      props.supplierHearing?.addStsCdSupplierMaxCapacity === '3' &&
      ((props.supplierHearing?.addStsCdHearingSheet === '3' && props.hearingSheetFlg === 1) || props.hearingSheetFlg === 0)
    ) {
      props.handleOnId('I010')
      props.handleOnMessageKey('回答送信')
      props.handleOnConfirmDialogshow(true)
    } else {
      const checkMessage: string[] = []
      if (props.supplierHearing?.addStsCdSupplierDecisionBuilding !== '3') {
        checkMessage.push('業者決定物件')
      }
      if (props.supplierHearing?.addStsCdSupplierEstimationBuilding !== '3') {
        checkMessage.push('見積中物件')
      }
      if (props.supplierHearing?.addStsCdOtherCompanyBuilding !== '3') {
        checkMessage.push('他社物件')
      }
      if (props.supplierHearing?.addStsCdSupplierMaxCapacity !== '3') {
        checkMessage.push('業者最大能力')
      }
      if (props.supplierHearing?.addStsCdHearingSheet !== '3' && props.hearingSheetFlg === 1) {
        checkMessage.push('ヒアリングシート')
      }
      const message: ErrorMessage[] = [{
        id: 'E017',
        content: MessageProvider.provide(
          'E017', checkMessage.join(',')
        )
      }]
      props.setErrorMessages(message)
    }
  }

  /**
   * 修正内容
   */
  const changeFixContent = (event: TextAreaChangeEvent) => {
    props.setIsChanged(true)
    props.handleOnSupplierHearing(
      produce((draft) => {
        if (draft !== undefined) {
          draft.fixContent = event.value
        }
      })
    )
  }

  /** 添付資料アップロードボタン */
  const handleUpload = (event: ChangeEvent<HTMLInputElement>) => {
    if (
      event.target.value.substring(event.target.value.length - 5) === '.xlsx' ||
      event.target.value.substring(event.target.value.length - 4) === '.xls'
    ) {
      // 添付資料ファイルパスの更新
      if (event.target?.files != null && event.target.files?.length === 1) {
        // アップロード用にタイムスタンプを付けた形式のファイル名に作り直す
        const file = event.target.files[0]
        const blob = file.slice(0, file.size, file.type)
        const uploadFilename = `${dayjs().format('YYYYMMDDhhmmss')}_${file.name}`
        const uploadFile = new File([blob], uploadFilename, { type: file.type })

        props.setIsChanged(true)
        props.handleOnSupplierHearing(
          produce((draft) => {
            if (draft !== undefined) {
              draft.attachmentFilePath = event.target.files?.length === 1 ? uploadFilename : ''
            }
          })
        )

        // アップロードファイルの設定
        props.setAttachmentFile(uploadFile)
        props.setAttachmentFileName(file.name)
      }
      return
    }

    const message: ErrorMessage[] = [{
      id: 'E010',
      content: MessageProvider.provide(
        'E010', '「.xls」または「.xlsx」'
      )
    }]
    props.setErrorMessages(message)
  }

  /** 添付資料ダウンロードボタン */
  const handleDownload = () => {
    new BlobFileService()
      .download(
        'attached-files',
        props.supplierHearing === undefined
          ? ''
          : props.supplierHearing.attachmentFilePath ?? ''
      )
      .then((res) => {
        BlobFileService.downloadRedirectTo(res)
      })
      .finally(() => {})
  }

  /** 修正内容登録ボタン */
  const handleFixContentSubmit = () => {
    props.handleOnId('I007')
    props.handleOnArgslist(['修正内容登録'])
    props.handleOnMessageKey('修正内容登録')
    props.handleOnConfirmDialogshow(true)
  }

  return (
    <Card orientation="horizontal">
      <CardHeader>
        <CardTitle>
          <span
            style={{ fontSize: '1.5em', fontWeight: 'bold', color: 'gray' }}
          >
            {replyYm}
          </span>
        </CardTitle>
        <CardSubtitle>
          <span
            style={{ fontSize: '1.5em', fontWeight: 'bold', color: 'gray' }}
          >
            {expense}
          </span>
        </CardSubtitle>
        <CardSubtitle>
          <span
            style={{ fontSize: '1.5em', fontWeight: 'bold', color: 'gray' }}
          >
            {props.supplierHearing?.replySts}{' '}
          </span>
        </CardSubtitle>
      </CardHeader>
      <CardBody>
        <GridLayout gap={{ rows: 10, cols: 0 }}>
          <GridLayoutItem col={1} row={1}>
            <span style={{ fontWeight: 'bold', fontSize: '1.5em' }}>
              {branchNm}
            </span>
          </GridLayoutItem>
          <GridLayoutItem col={2} colSpan={5} row={1}>
            <span style={{ fontWeight: 'bold', fontSize: '1.5em' }}>
              {supplierNm}
              {' '}{supplierBranchNm !== '' && supplierBranchNm !== null ? supplierBranchNm : ''}
              {factoryNm !== '' ? `${factoryNm}` : ''}
            </span>
          </GridLayoutItem>

          <GridLayoutItem col={1} row={2} rowSpan={2}>
            <div style={{ width: '250px' }}>
              <Grid
                style={{ maxHeight: '130px' }}
                data={historyList}
                rowRender={rowRender}
              >
                <GridColumn field="replySts" title=" 回答状況" />
                <GridColumn field="stsUpdDtm" title="更新日" />
              </Grid>
            </div>
          </GridLayoutItem>

          <GridLayoutItem col={2} row={2}>
            <TextArea
              rows={4}
              placeholder={'回答コメントを入力して下さい。'}
              style={{ width: '20vw' }}
              readOnly={mode !== '1' || props.lastMonthFlg || props.isNotOwnBranch}
              value={props.supplierHearing?.replyComment ?? undefined}
              onChange={changeReplyComment}
            />
          </GridLayoutItem>
          <GridLayoutItem col={2} row={3}>
            <Input
              style={{ width: '10vw' }}
              placeholder={'回答者(姓)'}
              disabled={mode === '3' || (mode === '2' && userProp?.authority === 5) || props.lastMonthFlg || props.isNotOwnBranch}
              value={props.supplierHearing?.replyNmLast ?? undefined}
              onChange={changeReplyNmLast}
            />
            <Input
              style={{ width: '10vw' }}
              placeholder={'回答者(名)'}
              disabled={mode === '3' || (mode === '2' && userProp?.authority === 5) || props.lastMonthFlg || props.isNotOwnBranch}
              value={props.supplierHearing?.replyNmFirst ?? undefined}
              onChange={changeReplyNmFirst}
            />
            <Button
              style={{ marginLeft: '5px', minWidth: '120px' }}
              themeColor="primary"
              disabled={mode !== '1' || props.lastMonthFlg || props.isNotOwnBranch}
              onClick={answerTransmission}
            >
              回答送信
            </Button>
          </GridLayoutItem>

          <GridLayoutItem col={3} row={2}>
            <TextArea
              rows={4}
              placeholder={'修正内容を入力して下さい。'}
              style={{ width: '20vw' }}
              readOnly={mode === '3' || userProp?.authority === 5 || props.lastMonthFlg || props.isNotOwnBranch}
              value={props.supplierHearing?.fixContent ?? undefined}
              onChange={changeFixContent}
            />
          </GridLayoutItem>

          <GridLayoutItem col={3} row={3}>
            <Input
              style={{ width: '15vw' }}
              disabled
              placeholder={'添付資料を選択して下さい'}
              value={
                // ユーザに表示するファイル名は本来の（タイムスタンプが付与されていない）ファイル名で表示する
                (props.supplierHearing?.attachmentFilePath ?? undefined) !== undefined
                  ? props.supplierHearing?.attachmentFilePath?.substring(props.supplierHearing?.attachmentFilePath.indexOf('_') + 1, props.supplierHearing?.attachmentFilePath?.length)
                  : undefined
              }
            />
            <Button
              style={{ marginLeft: '5px' }}
              icon="upload"
              disabled={mode === '3' || userProp?.authority === 5 || props.lastMonthFlg || props.isNotOwnBranch}
            />
            <Button
              style={{ marginLeft: '5px' }}
              icon="download"
              themeColor="primary"
              disabled={(props.supplierHearing?.attachmentFilePath ?? '') === '' || props.attachmentFile !== undefined || props.lastMonthFlg || props.isNotOwnBranch}
              onClick={handleDownload}
            />
            <input
              style={{
                marginLeft: '5px',
                width: '25px',
                opacity: '0',
                position: 'relative',
                left: '-66px'
              }}
              disabled={mode === '3' || userProp?.authority === 5 || props.lastMonthFlg || props.isNotOwnBranch}
              title=""
              type="file"
              onChange={handleUpload}
              accept="application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            />
            <Button
              style={{ marginLeft: '5px', minWidth: '120px' }}
              themeColor="primary"
              disabled={mode === '3' || userProp?.authority === 5 || props.lastMonthFlg || props.isNotOwnBranch}
              onClick={handleFixContentSubmit}
            >
              修正内容登録
            </Button>
          </GridLayoutItem>
        </GridLayout>
      </CardBody>
    </Card>
  )
}
